import type { ProjectListItem } from '@shared/types/project';
import InlineEditableName from '~/components/ui/InlineEditableName';

interface ProjectCardProps {
  project: ProjectListItem;
  onDelete: (id: string) => void;
  onClick: (id: string) => void;
  onRename: (id: string, name: string) => void;
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const seconds = Math.floor((now - then) / 1000);

  if (seconds < 60) {
    return 'just now';
  }

  const minutes = Math.floor(seconds / 60);

  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.floor(hours / 24);

  if (days < 30) {
    return `${days}d ago`;
  }

  return new Date(dateStr).toLocaleDateString();
}

// Deterministic accent color from project name (no random thumbnails / fake screenshots).
const THUMB_PALETTE = [
  { bg: '#1A2330', accent: '#6AB8F2' },
  { bg: '#E8DFD3', accent: '#8B6F4E' },
  { bg: '#E5E2D8', accent: '#5C7A4F' },
  { bg: '#15151A', accent: '#F2EDE6' },
  { bg: '#FAF6E8', accent: '#C49B5A' },
  { bg: '#0F1419', accent: '#6EE7A6' },
  { bg: '#3A2418', accent: '#E0C9A8' },
  { bg: '#0E0E12', accent: '#D9F26A' },
];

function thumbColors(seed: string): { bg: string; accent: string } {
  let hash = 0;

  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }

  return THUMB_PALETTE[Math.abs(hash) % THUMB_PALETTE.length];
}

export default function ProjectCard({ project, onDelete, onClick, onRename }: ProjectCardProps) {
  const { bg, accent } = thumbColors(project.name);

  return (
    <div
      onClick={() => onClick(project.id)}
      className="group bg-surface border border-hairline rounded-xl overflow-hidden cursor-pointer hover:border-subtle can-hover:hover:-translate-y-0.5 transition-all duration-200"
    >
      {/* Thumb — stylized mini-preview, not a real screenshot */}
      <div className="relative aspect-[16/10] overflow-hidden border-b border-hairline" style={{ background: bg }}>
        <div className="h-full p-3.5 flex flex-col gap-2">
          <div className="h-1.5 w-15 rounded-sm opacity-60" style={{ background: accent }} />
          <div
            className="font-instrument text-[18px] leading-[1.05] tracking-[-0.02em] max-w-[70%]"
            style={{ color: accent }}
          >
            {project.name}
          </div>
          <div className="flex gap-1 mt-auto">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex-1 aspect-[4/5] rounded-sm opacity-20" style={{ background: accent }} />
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-3.5">
        <div className="flex items-center gap-2 mb-1.5">
          <InlineEditableName
            value={project.name}
            onSave={(name) => onRename(project.id, name)}
            className="font-geist-mono text-[13px] text-primary flex-1"
            inputClassName="font-geist-mono text-[13px] text-primary bg-sunken border border-accent rounded px-1.5 py-0.5 outline-none w-full"
          />
          {project.isPublic && (
            <span className="inline-flex items-center gap-1 px-2 py-[3px] rounded-full text-[10px] font-medium uppercase tracking-[0.04em] text-accent bg-accent/[0.06] border border-accent/40">
              <span className="ui-dot ui-dot-live" />
              public
            </span>
          )}
        </div>
        <div className="text-[12px] text-secondary mb-2.5 min-h-[18px] truncate">{project.description || '—'}</div>
        <div className="flex items-center gap-2 text-[11px] text-muted">
          <span>{timeAgo(project.lastAccessedAt)}</span>
          <span className="text-faint">·</span>
          <span className="font-geist-mono flex-1 truncate">
            {project.chatCount} {project.chatCount === 1 ? 'chat' : 'chats'}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(project.id);
            }}
            className="ui-btn-icon -mr-1.5 hover:text-danger"
            title="Delete project"
          >
            <span className="i-ph:trash text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}
